import { useEffect } from "react";
import { Clock, EnvelopeSimple, MapPin, Phone, X } from "@phosphor-icons/react";
import LeadForm from "@/pages/LeadForm";
import { ArrowLink } from "@/components/site/Motion";

export const OPEN_CONTACT_EVENT = "site:contact";
export const openContactPanel = () => window.dispatchEvent(new CustomEvent(OPEN_CONTACT_EVENT));

// Offcanvas "Get in touch" panel (Maiko "Communications"): address, hours, phone, form.
export const ContactPanel = ({ open, onClose, site }) => {
  const shop = site?.shop || {};
  const contact = site?.config?.contact || {};
  const hours = shop.hours_lines?.length
    ? shop.hours_lines
    : (contact.hours || []).filter(Boolean);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <div
      className={`fixed inset-0 z-[80] overflow-hidden ${open ? "" : "pointer-events-none"}`}
      aria-hidden={!open}
      inert={open ? undefined : true}
      data-testid="site-contact-panel-root"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-testid="site-contact-panel-backdrop"
        className={`absolute inset-0 bg-[#1a1a18]/50 transition-opacity duration-500 ${open ? "opacity-100" : "opacity-0"}`}
      />
      <aside
        data-testid="site-contact-panel"
        className={`absolute inset-y-0 right-0 flex w-full max-w-[520px] flex-col overflow-y-auto bg-[color:var(--s-bg)] px-8 pb-12 pt-7 text-[color:var(--s-ink)] shadow-[-40px_0_80px_-40px_rgba(0,0,0,0.5)] transition-transform duration-600 ease-[cubic-bezier(.2,.7,.2,1)] sm:px-12 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between">
          <span className="s-micro text-[color:var(--s-mute)]">{shop.name || "Brioni Granite"}</span>
          <button
            type="button"
            onClick={onClose}
            data-testid="site-contact-panel-close"
            className="s-micro flex items-center gap-2 transition-colors duration-300 hover:text-[color:var(--s-gold)]"
          >
            Close <X size={16} weight="bold" />
          </button>
        </div>

        <h2 className="s-hx mt-12 text-4xl sm:text-5xl" data-testid="site-contact-panel-title">
          Get in <strong>touch.</strong>
        </h2>
        <p className="mt-5 max-w-sm text-sm leading-relaxed text-[color:var(--s-mute)]">
          {contact.body || "Send your plans or a rough sketch — we come back with a written quote."}
        </p>

        <ul className="mt-10 space-y-5 text-sm">
          {shop.address && (
            <li className="flex items-start gap-4" data-testid="site-contact-panel-address">
              <MapPin size={18} weight="light" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.address)}`}
                target="_blank"
                rel="noreferrer"
                className="whitespace-pre-line leading-relaxed hover:text-[color:var(--s-gold)]"
              >
                {shop.address}
              </a>
            </li>
          )}
          {shop.phone && (
            <li className="flex items-start gap-4" data-testid="site-contact-panel-phone">
              <Phone size={18} weight="light" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
              <a href={`tel:${shop.phone_link || shop.phone}`} className="hover:text-[color:var(--s-gold)]">
                {shop.phone}
              </a>
            </li>
          )}
          {shop.email && (
            <li className="flex items-start gap-4" data-testid="site-contact-panel-email">
              <EnvelopeSimple size={18} weight="light" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
              <a href={`mailto:${shop.email}`} className="break-all hover:text-[color:var(--s-gold)]">
                {shop.email}
              </a>
            </li>
          )}
          {hours.length > 0 && (
            <li className="flex items-start gap-4" data-testid="site-contact-panel-hours">
              <Clock size={18} weight="light" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
              <span className="leading-relaxed text-[color:var(--s-mute)]">
                {hours.map((h) => (
                  <span key={h} className="block">
                    {h}
                  </span>
                ))}
              </span>
            </li>
          )}
        </ul>

        <div className="mt-10 border-t border-[color:var(--s-line)] pt-8">
          <div className="s-micro text-[color:var(--s-mute)]">Drop a line</div>
          <div className="lead-embedded mt-4" data-testid="site-contact-panel-form">
            {open && <LeadForm embedded onClose={onClose} />}
          </div>
        </div>

        <div className="mt-10">
          <ArrowLink to="/contact" testId="site-contact-panel-more" onClick={onClose}>
            Showroom, map &amp; hours
          </ArrowLink>
        </div>
      </aside>
    </div>
  );
};
